import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import CoraaAIDialog from "./CoraaAIDialog";

interface MeetCoraaSectionProps {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  setInitialPrompt: (prompt: string) => void;
}

const MeetCoraaSection: React.FC<MeetCoraaSectionProps> = ({
  dialogOpen,
  setDialogOpen,
  setInitialPrompt,
}) => {
  const prompts = [
    "What can we do for World Oceans Day in Mumbai?",
    "Find youth skilling campaigns in Tier 2 cities",
    "Suggest kits for in-office Earth Week activities",
    "Recommend low-cost DIY kits for World Plastic Bag Free Day",
    "Find women-led NPOs in education",
    "Benchmark CSR spend vs IT peers",
    "Generate CSR Q2 impact summary as Graph",
    "Find onboarding campaigns for interns",
    "What’s trending for International Youth Day?",
    "Suggest 30-person virtual events this Friday",
    "Suggest events under ₹400 in Hyderabad",
    "Show me eco-campaigns with measurable ROI",
    "Suggest a high-impact city event next week",
    "Find partners for a coastal clean-up in India",
    "How do FMCGs celebrate Independence Day?",
  ];

  // continuous scroll ref and effect
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollSpeed = 0.5; // px per tick (~60fps)
    let offset = el.scrollTop;
    const halfHeight = el.scrollHeight / 2;
    const intervalId = window.setInterval(() => {
      offset += scrollSpeed;
      if (offset >= halfHeight) offset -= halfHeight;
      el.scrollTop = offset;
    }, 16);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <section className="py-20 bg-[#0d1927] text-white" id="meet-coraa">
      <div className="container mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
        {/* Left: Headline + Features */}
        <div>
          <button
            onClick={() => setDialogOpen(true)}
            className="inline-block bg-gradient-to-r from-yellow-400 via-pink-500 to-red-500 text-white text-xs md:text-sm px-4 py-2 rounded-full font-bold shadow mb-4 tracking-wider animate-bounce cursor-pointer hover:shadow-lg transition-shadow"
          >
            MEET CORAA
          </button>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight text-white drop-shadow-xl">
            Your{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-pink-500 to-red-500">
              AI Co-Pilot
            </span>{" "}
            for Corporate Social Impact
          </h2>
          <p className="text-lg text-white/80 mb-6">
            No forms. No delays. Just ask CORAA, and launch verified
            volunteering campaigns with perfect impact alignment, automated
            communication, and instant eports.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="bg-gradient-to-br from-[#232b41] via-[#1a2235] to-[#2d1a2e] rounded-xl p-4 shadow-lg border border-white/10">
              <h4 className="font-semibold text-white mb-1">
                💬 Chat to Launch
              </h4>
              <p className="text-white/80 text-sm">
                “What can my team do for Environment Day in Pune?” CORAA replies
                instantly with budget-aligned ideas.
              </p>
            </div>
            <div className="bg-gradient-to-br from-[#232b41] via-[#1a2235] to-[#2d1a2e] rounded-xl p-4 shadow-lg border border-white/10">
              <h4 className="font-semibold text-white mb-1">
                🎯 Alignment On Tap
              </h4>
              <p className="text-white/80 text-sm">
                Match events to your SDGs, themes, and internal CSR Pillars,
                instantly.
              </p>
            </div>
            <div className="bg-gradient-to-br from-[#232b41] via-[#1a2235] to-[#2d1a2e] rounded-xl p-4 shadow-lg border border-white/10">
              <h4 className="font-semibold text-white mb-1">
                📊 Impact On Autopilot
              </h4>
              <p className="text-white/80 text-sm">
                Auto-generate impact reports. Track outcomes by location, spend,
                or team.
              </p>
            </div>
            <div className="bg-gradient-to-br from-[#232b41] via-[#1a2235] to-[#2d1a2e] rounded-xl p-4 shadow-lg border border-white/10">
              <h4 className="font-semibold text-white mb-1">
                ⚡ Zero Setup Time
              </h4>
              <p className="text-white/80 text-sm">
                No logins. No onboarding. CORAA just works, right out of the
                box.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="inline-block bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition"
            onClick={() => setDialogOpen(true)}
          >
            Try CORAA Live →
          </button>
          <div className="text-xs text-white/60 mt-2"></div>
        </div>

        {/* Right: Mascot + Animated Chat */}
        <div className="flex flex-col items-center gap-4">
          <button
            onClick={() => setDialogOpen(true)}
            className="group relative cursor-pointer transition-transform hover:scale-105"
          >
            <img
              src="/images/coraa-mascot.png"
              alt="CORAA Mascot"
              className="w-36 h-36 rounded-full border-4 border-[#e0f2f1] bg-[#f8fafc] shadow object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-white font-bold">Ask CORAA</span>
            </div>
          </button>
          <div className="w-[340px] max-w-full bg-white rounded-2xl shadow-xl border border-[#e0f2f1] overflow-hidden min-h-[20px]">
            <div className="flex items-center gap-2 bg-[#e8f5e9] px-4 py-2 border-b border-[#e0f2f1]">
              <span className="w-6 h-6 rounded-full bg-[#2E7D32] flex items-center justify-center text-white font-bold">
                C
              </span>
              <span className="font-semibold text-[#2E7D32]">CORAA</span>
              <span className="text-xs bg-[#2E7D32] text-white px-2 py-1 rounded ml-auto">
                AI
              </span>
            </div>

            <div className="relative h-48 overflow-hidden bg-[#f8fafc]">
              <div
                ref={scrollRef}
                className="h-full overflow-y-auto scrollbar-hide"
              >
                <div className="flex flex-col gap-2 px-4 py-2 text-sm text-gray-800">
                  {prompts.concat(prompts).map((text, i) => (
                    <button
                      key={i}
                      className="bg-white rounded-lg px-3 py-2 shadow w-fit hover:bg-green-100 transition-colors border border-transparent hover:border-green-400 cursor-pointer text-left"
                      onClick={() => {
                        setInitialPrompt(text);
                        setDialogOpen(true);
                      }}
                    >
                      “{text}”
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* The dialog itself is now managed at the GIVFPage level, so remove the local dialog instance here. */}
    </section>
  );
};

export default MeetCoraaSection;
